/**
 * coverExpiry — when a roadworthy certificate or an insurance policy stops being good.
 *
 * THE OWNER'S RULING (G6, 2026-10-17): a certificate or policy is good THROUGH its expiry date,
 * judged on the Ghana day, and one rule applies everywhere. So it is expired only once the Ghana
 * day is AFTER the expiry date, it expires within 30 days while 0 to 30 Ghana days are left
 * (0 = its last day of cover), and it is valid beyond that. Every screen that labels or counts a
 * certificate or a policy by its expiry asks this file, so no two screens can answer differently.
 *
 * WHY THE CALENDAR DAY AND NOT THE INSTANT. Every `expires_at` is stored at 00:00 UTC on the last
 * day of cover (57 of 57 certificates, 374 of 374 policies, production). Comparing that instant
 * with `new Date()` calls a policy expired from the first second of the day it still covers, and
 * counting whole 24-hour periods reads 31 days left as 30. Both ends are therefore taken to the
 * Ghana calendar day through `businessDay`, the codebase's one notion of the business day, and
 * only then compared.
 *
 * THE INSPECTION SCREENS ALREADY WORKED THIS WAY (`expires_on < today`, on the server's Ghana
 * `today`), so `today` may be given as that 'yyyy-MM-dd' string as well as an instant.
 *
 * PURE, AND REACT-FREE — vitest runs node-env over `.ts` only.
 */

import { addDays, differenceInCalendarDays } from 'date-fns';

import { businessDay, businessDayStartInstant } from '@/lib/format';

/** The "expires within 30 days" window, counted in Ghana days and inclusive: 30 days left is inside. */
export const EXPIRING_WITHIN_DAYS = 30;

export type CoverExpiryStatus = 'expired' | 'expiring' | 'valid';

/**
 * Ghana days from today to the last day of cover: 0 on the expiry date itself, 1 the day before,
 * -1 the day after. Null when either value is missing or unparseable.
 *
 * `expiresAt` is a `timestamptz` as PostgREST returns it, or a `date` column's 'yyyy-MM-dd'.
 * `today` is either an instant (default: now) or a 'yyyy-MM-dd' Ghana day such as the inspection
 * RPCs' `today`. Do not pass a calendar-day Date such as `businessToday()`: a Date is read as an
 * instant.
 */
export function daysToExpiry(
    expiresAt: string | Date | null | undefined,
    today: string | Date = new Date(),
): number | null {
    const expiry = businessDay(expiresAt);
    const day = businessDay(today);
    if (!expiry || !day) return null;
    return differenceInCalendarDays(expiry, day);
}

/** Expired, expiring within 30 days, or valid, by the G6 rule. Null when there is no readable expiry. */
export function coverExpiryStatus(
    expiresAt: string | Date | null | undefined,
    today: string | Date = new Date(),
): CoverExpiryStatus | null {
    const days = daysToExpiry(expiresAt, today);
    if (days === null) return null;
    if (days < 0) return 'expired';
    if (days <= EXPIRING_WITHIN_DAYS) return 'expiring';
    return 'valid';
}

/**
 * The same rule as instant bounds, for a filter the database applies to `expires_at`:
 * expired is `expires_at < expiredBefore`, expiring is `expiredBefore <= expires_at < validFrom`,
 * and valid is `expires_at >= validFrom`.
 *
 * `expiredBefore` is the instant today opens in Ghana, and `validFrom` the instant the day 31 days
 * from today opens, so a stored expiry at any time of its day lands in the bucket its Ghana day
 * belongs to.
 */
export function coverExpiryBounds(now: Date = new Date()): { expiredBefore: string; validFrom: string } {
    const today = businessDay(now)!;
    return {
        expiredBefore: businessDayStartInstant(today),
        validFrom: businessDayStartInstant(addDays(today, EXPIRING_WITHIN_DAYS + 1)),
    };
}
